// Safe, mechanical (non-fuzzy) normalization used to compare a scraped eBuySugar
// tender row against a SugarHathi Mandi offer. Both sides run through the SAME
// function, so a match only happens when the two strings become byte-identical
// after case/punctuation/whitespace normalization — never a "looks similar" guess.
// Verified against real production data: mi_mills canonical_name for at least one
// mill ("Ashok Sahakari Sakhar Karkhana Ltd., Ashoknagar, Maharashtra") is byte-identical
// to the eBuySugar mill_name for the same factory, so comparing full normalized names
// (not just a truncated prefix) is the right granularity here.
export function normalizeFactoryName(raw) {
  if (!raw) return '';
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Case + "/" -> "-" only. Deliberately does NOT expand "S1"/"S2" to "S1-30"/"S2-30",
// does NOT merge "M1"/"M2" into "M-30", and does NOT touch "Refined *" grades — those
// stay distinct so a genuinely different grade can never present a false Buy Now match.
export function normalizeGrade(raw) {
  if (!raw) return '';
  return raw.trim().toUpperCase().replace(/\//g, '-').replace(/\s+/g, ' ');
}

// Buy Now target: the real Mandi factory page, carrying the exact matched grade
// (quote id first, grade string as a fallback) plus a one-shot autoBuy flag so the
// destination page can jump straight into its own existing Buy Sugar action — same
// purchase mechanism as /mandi, never a separate checkout. Shared by both the Live
// Tender Info listing and any per-factory Buy Now link so the URL shape never drifts.
export function buildBuyNowUrl({ mandiFactorySlug, mandiGradeCode, mandiQuoteId }) {
  const params = new URLSearchParams();
  if (mandiQuoteId) params.set('quoteId', mandiQuoteId);
  if (mandiGradeCode) params.set('grade', mandiGradeCode);
  params.set('autoBuy', '1');
  return `/mandi/${mandiFactorySlug}?${params.toString()}`;
}

// Mandi -> Buyer Marketplace continuation. Mandi's mill_price_quotes.id has no
// foreign key into the Marketplace's quotas table, so this carries what Mandi
// DOES know (factory name + grade) for the Marketplace to resolve against its
// own loaded offers via normalizeFactoryName/normalizeGrade above; mandiQuoteId
// rides along for source traceability only, never as a lookup key. Shared by
// both Mandi entry points (grid + factory detail) so the destination URL shape
// never drifts between them.
export function buildMarketplaceBuyUrl({ factoryName, grade, mandiQuoteId }) {
  const params = new URLSearchParams();
  if (factoryName) params.set('factory', factoryName);
  if (grade) params.set('grade', grade);
  if (mandiQuoteId) params.set('quoteId', mandiQuoteId);
  params.set('source', 'mandi');
  return `/sugar-marketplace-dashboard?${params.toString()}`;
}
